import { useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Checkbox, IconButton, Tooltip } from "@material-ui/core";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent, ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { dateTimeZoneConverter } from "../../../../../utils/commonFunctions/date";

import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";

import { IAdminDocument } from "../../../../../@types/documentAdmnstration.types";

import MoreVertIcon from "@material-ui/icons/MoreVert";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import Folder from "@material-ui/icons/Folder";
import { Image } from "@material-ui/icons";
import { handleNameCellRenderer } from "../../../../../utils/commonFunctions/cellRenderers";
import MenuList from "../menuList/MenuList";
import { LazyCustomImageDialog } from "../../../../shared";
import { IDocumentBucketProps } from "../../../../../@types/components/documentBucketProps.types";
import FileInfoDialog from "../dialog/fileInfoDialog/FileInfoDialog";
import { AuthContext } from "../../../../../contexts";

const DocumentsList = ({
	gridData,
	imageDialogProps,
	handleChangeFolder,
	setDocumentList,
	setAllDocumentList,
	handleViewImageChange
}: IDocumentBucketProps) => {
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);

	// eslint-disable-next-line prettier/prettier
	const navigate = useNavigate();
	const agGridRef = useRef<any>(null);

	const { user } = useContext(AuthContext);

	const permission = (user as any)?.permissions.filter((permission: any) => permission.group_number === 0);
	const documentSharingPermission =
		permission && permission.length > 0
			? permission[0]?.permission_and_subscription.permissions.settings_menu.document_sharing_sub.self
			: null;

	const handleAgGridReady = (params: GridReadyEvent) => {
		const { api, columnApi } = params;
		setAgGridAPI(api);
		setAgGridColumnAPI(columnApi);
		api.sizeColumnsToFit();
	};
	const handleFirstDataRendered = (params: any) => null;

	const handleDateCellRenderer = ({ value }: { value: any }) => {
		const date = dateTimeZoneConverter(value as string) as string;
		return <div>{value ? date : "N/A"}</div>;
	};

	// const ;

	const HandleActionCellRenderer = (value: any) => {
		const { data } = value;
		const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
		const open = Boolean(anchorEl);

		const handleClick = (event: React.MouseEvent<HTMLElement>) => {
			setAnchorEl(event.currentTarget);
		};

		const handleClose = () => {
			setAnchorEl(null);
		};

		return (
			<div>
				{data.type === "folder" ? (
					<>
						<Tooltip title="Open" enterDelay={500} leaveDelay={200} arrow>
							<IconButton onClick={() => handleChangeFolder(data._id, data.name)}>
								<ArrowForwardIosIcon />
							</IconButton>
						</Tooltip>
						{documentSharingPermission &&
							(documentSharingPermission.update || documentSharingPermission.delete) && (
								<Tooltip title="More" enterDelay={500} leaveDelay={200} arrow>
									<IconButton aria-controls="fade-menu" aria-haspopup="true" onClick={handleClick}>
										<MoreVertIcon />
									</IconButton>
								</Tooltip>
							)}
					</>
				) : (
					<>
						<Tooltip title="View" enterDelay={500} leaveDelay={200} arrow>
							<IconButton onClick={() => handleViewImageChange(data)}>
								<VisibilityIcon />
							</IconButton>
						</Tooltip>
						{documentSharingPermission &&
							(documentSharingPermission.update || documentSharingPermission.delete) && (
								<Tooltip title="Upload" enterDelay={500} leaveDelay={200} arrow>
									<IconButton aria-controls="fade-menu" aria-haspopup="true" onClick={handleClick}>
										<MoreVertIcon />
									</IconButton>
								</Tooltip>
							)}
					</>
				)}
				<MenuList
					anchorEl={anchorEl}
					type={data.type}
					handleClick={handleClick}
					handleClose={handleClose}
					open={open}
					documents={gridData}
					setDocumentList={setDocumentList}
					setAllDocumentList={setAllDocumentList}
					adminDocument={data}
				/>
			</div>
		);
	};

	const handleSelectDocument = () => {
		return (
			<div>
				<Checkbox />
			</div>
		);
	};

	const handleDocumentNameCellRenderer = (value: any) => {
		const { data } = value;
		return (
			<div style={{ cursor: "pointer" }}>
				{data.type === "folder" ? (
					<div className="document-name-cell" onDoubleClick={() => handleChangeFolder(data._id, data.name)}>
						<Folder style={{ color: "orange" }} />
						{data.name}
					</div>
				) : (
					<div className="document-name-cell">
						<Image style={{ color: "#44b700" }} />
						{data.name}
					</div>
				)}
			</div>
		);
	};

	const handleRegisteredCellRender = ({ value }: { value: boolean }) => {
		return <div>{value ? "Yes" : "No"}</div>;
	};

	const handleApprovedByCellRender = ({ value }: { value: any }) => {
		return <div>{typeof value === typeof {} ? value.user_name : value}</div>;
	};

	return (
		<div className="user-management-grid-container glb-tab-grid-container">
			<div className="ag-theme-alpine" style={{ height: "400px", width: "100%" }}>
				<FileInfoDialog
					image={imageDialogProps.image}
					mimeType={imageDialogProps.mimeType}
					handleCloseCallback={imageDialogProps.handleCloseCallback}
					openDialog={imageDialogProps.openDialog}
					purpose={imageDialogProps.details}
					title={imageDialogProps.title}
					key={0}
				/>
				<AgGridReact
					ref={agGridRef}
					rowData={gridData}
					onGridReady={handleAgGridReady}
					onFirstDataRendered={handleFirstDataRendered}
					animateRows
					rowDragManaged
					frameworkComponents={{
						handleDateCellRenderer: handleDateCellRenderer,
						handleApprovedByCellRender: handleApprovedByCellRender,
						handleRegisteredCellRender: handleRegisteredCellRender
					}}
					defaultColDef={{
						filter: "agTextColumnFilter",
						floatingFilter: true,
						resizable: true,
						floatingFilterComponentParams: { suppressFilterButton: true }
					}}
					defaultColGroupDef={{ marryChildren: true }}
					rowSelection="single"
					onCellValueChanged={(params: CellValueChangedEvent) => {
						params.api.refreshCells({
							force: true,
							columns: ["assign_group", "user_information"]
						});
					}}
					enableCellChangeFlash={true}
				>
					<AgGridColumn
						field="name"
						floatingFilterComponentParams={{ suppressFilterButton: true }}
						cellRendererFramework={handleDocumentNameCellRenderer}
						headerName="Name"
					></AgGridColumn>
					<AgGridColumn
						field="createdAt"
						floatingFilterComponentParams={{ suppressFilterButton: true }}
						cellRenderer="handleDateCellRenderer"
						headerName="Uploaded On"
					></AgGridColumn>
					<AgGridColumn
						field="group_owner_details"
						headerName="Uploaded By"
						cellRendererFramework={handleNameCellRenderer}
					></AgGridColumn>
					<AgGridColumn field="group_owner_details.role" headerName="Role"></AgGridColumn>

					<AgGridColumn
						field="type"
						filter={false}
						cellRendererFramework={HandleActionCellRenderer}
						headerName=""
					></AgGridColumn>
				</AgGridReact>
			</div>
		</div>
	);
};

export default DocumentsList;
